import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "antelli-admin-passcode";

export default function Enquiries() {
  const [passcode, setPasscode] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [enquiries, setEnquiries] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (code) => {
    if (!code) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/enquiries`, {
        headers: { "X-Admin-Passcode": code },
      });
      setEnquiries(res.data);
      sessionStorage.setItem(STORAGE_KEY, code);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setPasscode("");
        setEnquiries(null);
        setError("That passcode isn't right.");
      } else {
        setError("Couldn't load enquiries just now.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) load(saved);
  }, [load]);

  const onSubmit = (e) => {
    e.preventDefault();
    load(passcode);
  };

  const clear = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPasscode("");
    setEnquiries(null);
    setError("");
  };

  return (
    <div data-testid="enquiries-page">
      <Seo title="Enquiries — Antelli" description="Private enquiries inbox." />
      <div className="max-w-3xl mx-auto px-6 sm:px-10 pt-24 pb-24 sm:pt-36 sm:pb-36">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7A6034]">Private</p>
          <h1
            data-testid="enquiries-heading"
            className="mt-6 font-serif-display text-4xl sm:text-5xl leading-tight text-[#1A1C23] font-medium"
          >
            Enquiries
          </h1>
        </Reveal>

        {enquiries === null ? (
          <Reveal delay={0.1}>
            <form onSubmit={onSubmit} data-testid="enquiries-passcode-form" className="mt-14 max-w-md">
              <label htmlFor="passcode" className="block text-xs uppercase tracking-[0.2em] text-[#757882] mb-1">
                Passcode
              </label>
              <input
                id="passcode"
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                autoComplete="current-password"
                data-testid="enquiries-passcode-input"
                className="w-full bg-transparent border-0 border-b border-[#1A1C23]/20 focus:border-[#987E54] focus:ring-0 focus:outline-none px-0 py-3 text-base text-[#1A1C23] transition-colors duration-300"
              />
              {error && (
                <p data-testid="enquiries-error" className="mt-4 text-sm text-[#8C4A3C]">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="enquiries-unlock-button"
                className="mt-8 inline-flex items-center border border-[#1A1C23] px-8 py-3.5 text-sm tracking-wide text-[#1A1C23] transition-all duration-300 hover:bg-[#1A1C23] hover:text-[#F4F3EF] disabled:opacity-50"
              >
                {loading ? "Checking…" : "View enquiries"}
              </button>
            </form>
          </Reveal>
        ) : (
          <div className="mt-14">
            <div className="flex items-center justify-between border-b border-[#1A1C23]/10 pb-6">
              <p className="text-sm text-[#757882]" data-testid="enquiries-count">
                {enquiries.length} {enquiries.length === 1 ? "message" : "messages"}
              </p>
              <button
                onClick={clear}
                data-testid="enquiries-lock-button"
                className="text-sm text-[#7A6034] hover:text-[#1A1C23] transition-colors duration-300"
              >
                Lock
              </button>
            </div>
            {enquiries.length === 0 ? (
              <p className="mt-10 text-base text-[#4A4D57]" data-testid="enquiries-empty">
                No enquiries yet.
              </p>
            ) : (
              <ul className="divide-y divide-[#1A1C23]/10">
                {enquiries.map((item) => (
                  <li key={item.id} data-testid={`enquiry-${item.id}`} className="py-8">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="font-serif-display text-xl text-[#1A1C23]">{item.name}</p>
                      <p className="text-xs text-[#757882]">
                        {item.created_at ? new Date(item.created_at).toLocaleString("en-AU") : ""}
                      </p>
                    </div>
                    <p className="mt-1 text-sm text-[#7A6034]">
                      {item.subject} —{" "}
                      <a href={`mailto:${item.email}`} className="border-b border-[#B69766]/50 hover:border-[#7A6034] transition-colors duration-300">
                        {item.email}
                      </a>
                    </p>
                    <p className="mt-3 text-base leading-relaxed text-[#4A4D57] whitespace-pre-wrap">
                      {item.message}
                    </p>
                    <a
                      href={`mailto:${item.email}?subject=${encodeURIComponent(`Re: Your Antelli enquiry — ${item.subject}`)}&body=${encodeURIComponent(`Hi ${item.name},\n\nThanks for getting in touch with Antelli.\n\n`)}`}
                      data-testid={`enquiry-reply-${item.id}`}
                      className="mt-4 inline-flex items-center border border-[#1A1C23]/30 px-5 py-2 text-xs tracking-wide text-[#1A1C23] transition-all duration-300 hover:bg-[#1A1C23] hover:text-[#F4F3EF]"
                    >
                      Reply by email
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
